/**
 * Relier à un appel d'offres la tâche d'un AUTRE : son destinataire doit voir l'AO.
 *
 * ## La règle (décision PO n° 66 du 2026-10-03, choix par défaut D213)
 *
 * Le serveur (`services/notifications/_destinataire_d_une_tache.juger_le_nouveau_lien`) refuse
 * en 422 `TASK_ASSIGNEE_OUT_OF_TENDER` de relier à un AO une tâche confiée à un collègue qui ne
 * voit pas l'entité de cet AO : la tâche nommerait une consultation qu'il ne peut pas ouvrir. La
 * modale de détail laissait pourtant relier n'importe quel AO, et n'apprenait le refus qu'au clic
 * sur « Enregistrer ».
 *
 * Ce crochet rejoue le MÊME jugement, sur la MÊME liste : `useDesignablesSurLaConsultation` lit
 * le prédicat du refus (`_designable_sur_l_entite`). Comme le serveur, il ne juge que si le lien
 * CHANGE vers un AO et que la tâche est à un autre : le lien inchangé ne rejuge rien, sa propre
 * tâche non plus.
 *
 * `refuse` n'est vrai que sur une liste LUE et COMPLÈTE : une liste en route, en échec ou
 * tronquée (`nonListes`) ne prouve pas l'absence, et le serveur juge de toute façon.
 * `enCours` : la liste est en route — l'écran attend avant d'enregistrer.
 */

import { useDesignablesSurLaConsultation } from '@/components/shared/useDesignablesSurLaConsultation'
import type { Notification } from '@/hooks/useNotifications.types'
import type { LinkedEntityValue } from '@/lib/linkedEntity'

import { aoDuLien } from './aoDuLien'

type Tache = Pick<Notification, 'recipient_id' | 'linked_entity_type' | 'linked_entity_id'>

export function useLienRefuseAuDestinataire({
    entityId,
    tache,
    lien,
    moi,
}: {
    /** L'entité de la session — celle sous laquelle la route lit l'AO. */
    entityId: string | null
    /** La tâche ENREGISTRÉE (son destinataire et son lien d'avant). */
    tache: Tache | null | undefined
    /** Le lien choisi dans la modale. */
    lien: LinkedEntityValue | null
    /** L'utilisateur connecté. */
    moi: string | null | undefined
}): { refuse: boolean; enCours: boolean } {
    const ao = aoDuLien(lien)
    const inchange = tache?.linked_entity_type === 'tender' && tache.linked_entity_id === ao
    const aJuger = Boolean(tache && ao && entityId && tache.recipient_id !== moi && !inchange)
    const { personnes, nonListes, isLoading, error } = useDesignablesSurLaConsultation({
        entityId: entityId ?? '',
        tenderId: ao,
        enabled: aJuger,
    })
    const lue = aJuger && !isLoading && !error && nonListes === 0
    return {
        refuse: lue && !personnes.some((p) => p.id === tache?.recipient_id),
        enCours: aJuger && isLoading,
    }
}
